import React, { useState } from "react";
import ActionFormatter from "Components/Table/ActionFormatter/ActionFormatter";
import Translator from "Languages/Utility/Translator";
import {
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import MuiButton from "Components/MuiButton/MuiButton";
import * as Icons from "react-bootstrap-icons";
import { read } from "Helpers/LocalStorage";
import StatusHandler from "Helpers/StatusHandler";
import { useTranslation } from "react-i18next";
import showNotify from "Helpers/Notify";
import BackDropLoading from "Components/BackDropLoading2/BackDropLoading";
import TicketingService from "Services/http/endpoints/superadmin/Ticketing";
import fileDownload from "js-file-download";
import TicketingMessage from "Components/TicketingMessage/TicketingMessage";
import TicketTextBox from "Components/TicketTextBox/TicketTextBox";
import { GETAGAINSATICKET } from "Stores/actionTypes";
import { useDispatch } from "react-redux";
import getFileTypeIcon from "Helpers/fileTypeIcon";
import MuiIconButton from "Components/MuiIconButton/MuiIconButton";

export default function ShowTicket({ row }) {
  const { direction } = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showDialog, setShowDialog] = useState(false);
  const [data, setData] = useState(null);
  const [newText, setNewText] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);

  const onShowDialog = () => {
    setShowDialog(true);
  };

  const onEnter = async () => {
    getData();
  };

  const onClear = () => {
    setNewText("");
    setSelectedFile(null);
  };

  const handleNewMessage = (e) => {
    setNewText(e.target.value);
  };

  const getData = async () => {
    onClear();
    try {
      setLoading(true);
      const params = {
        KeyGroup: read("groupAKey"),
        KeyUser: read("userAKey"),
        Id: row.Id,
        IsAdmin: true,
      };
      const response = await TicketingService.getOneTicketDetails(params);
      const err = StatusHandler(response);
      if (!err.isFail) {
        setData(response.data.Value);
        setTimeout(() => {
          try {
            const objDiv = document.getElementById(
              "dialog-content-ticket-details"
            );
            objDiv.scrollTop = objDiv.scrollHeight;
          } catch (err) {
            console.error("[catch error]", err);
          }
        }, 300);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
    }
  };

  const onClick = async (file) => {
    const params = {
      KeyGroup: read("groupAKey"),
      KeyUser: read("userAKey"),
      TicketId: row.Id,
      Id: file.Id,
      Type: file.Type,
      IsAdmin: true,
    };
    TicketingService.getTicketAttachment(params).then((response) => {
      const blob = new Blob([response.data], {
        type: "application/octet-stream",
      });
      fileDownload(blob, file.FileName);
    });
  };

  const onTicketPending = async () => {
    setLoading(true);
    const data = {
      KeyGroup: read("groupAKey"),
      KeyUser: read("userAKey"),
      Id: row.Id,
      IsAdmin: true,
    };
    const response = await TicketingService.putTicketPending(data);
    let err = StatusHandler(response);
    if (!err.isFail) {
      if (response.data.Value.IsError === true) {
        showNotify({ text: response.data.Value.Describ, customClass: "error" });
      } else {
        showNotify({ text: t("mission_accomplished"), customClass: "success" });
        onExit();
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const onTicketClose = async () => {
    setLoading(true);
    const data = {
      KeyGroup: read("groupAKey"),
      KeyUser: read("userAKey"),
      Id: row.Id,
      IsAdmin: true,
    };
    const response = await TicketingService.putTicketClose(data);
    let err = StatusHandler(response);
    if (!err.isFail) {
      if (response.data.Value.IsError === true) {
        showNotify({ text: response.data.Value.Describ, customClass: "error" });
      } else {
        showNotify({ text: t("mission_accomplished"), customClass: "success" });
        onExit();
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  const onChangeFile = ({ target }) => {
    setSelectedFile(target.files[0]);
  };

  const onDeleteFile = (e) => {
    e.preventDefault();
    setSelectedFile(null);
  };

  const onSaveMessage = async (e) => {
    e.preventDefault();
    setLoading(true);
    if (newText && newText !== "") {
      try {
        let bodyFormData = new FormData();
        bodyFormData.append("KeyGroup", read("groupAKey"));
        bodyFormData.append("KeyUser", read("userAKey"));
        bodyFormData.append("Description", newText);
        bodyFormData.append("Id", row.Id);
        bodyFormData.append("IsAdmin", true);
        bodyFormData.append("Attachment", selectedFile);

        const response = await TicketingService.postTicketMessage(bodyFormData);
        let err = StatusHandler(response);
        if (!err.isFail) getData();
      } catch (e) {
        showNotify({
          text: t("correctFormDataRequired"),
          customClass: "error",
        });
      }
    } else {
      if (newText === "" && selectedFile) {
        showNotify({
          text: t("needDescription"),
          customClass: "warning",
        });
      } else {
        showNotify({
          text: t("correctFormDataRequired"),
          customClass: "error",
        });
      }
    }
    setLoading(false);
  };

  const onExit = () => {
    setLoading(true);
    setShowDialog(false);
    setData(null);
    dispatch({ type: GETAGAINSATICKET, data: true });
  };

  return (
    <>
      <ActionFormatter
        title={<Translator id="ticket_details" />}
        icon={<Icons.CardList />}
        onClick={onShowDialog}
        className="primary-button"
      />
      <Dialog
        dir={direction}
        open={showDialog}
        onClose={onExit}
        TransitionProps={{ onEnter: onEnter, onExit: onExit }}
        fullWidth
        maxWidth="md"
      >
        <BackDropLoading loading={loading} />
        <div className="dialogHeader align-items-center">
          <p>
            <span className="ms-2">{row.Group}</span>
            <small>
              {`${row.Title}`}
            </small>
          </p>

          {data && (
            <div className="d-flex flex-row-reverse align-items-center justify-content-center g-2">
              <MuiIconButton
                onClick={onTicketClose}
                title={<Translator id="changeTicketToClose" />}
                className="primary-button"
                icon={<Icons.DoorClosed size={17} />}
                disabled={loading || data.IsClosed}
                hidden={data.IsClosed}
              />
              <MuiIconButton
                onClick={onTicketPending}
                title={<Translator id="changeTicketToChecking" />}
                className="primary-button"
                icon={<Icons.Clipboard2Check size={17} />}
                disabled={loading || data.IsClosed}
                hidden={data.IsClosed}
              />
              {data && (
                <div
                  className={data.IsClosed ? "ticket_closed" : "ticket_open"}
                >
                  <small>
                    {data.IsClosed ? "تیکت بسته می‌باشد" : "تیکت فعال می‌باشد"}
                  </small>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogContent id="dialog-content-ticket-details">
          {data && (
            <>
              <Grid container className="ticket-section-dialog-content">
                <Grid container className="ticket-page-info">
                  <Grid xs={12} item className="ticket-detail-info">
                    <span>{`${t("description")} :`}</span>
                    <p>{data.Descript}</p>
                  </Grid>
                  {data.File && (
                    <Grid xs={12} className="file-attachment">
                      <Grid className="d-flex flex-row g-2">
                        {`${t("fileAttachment")} :`}
                        <p>{data.File.FileName}</p>
                      </Grid>
                      <MuiButton
                        className="link"
                        title={t("viewFile")}
                        innerIcon={getFileTypeIcon(data.File.FileName)}
                        disabled={data.File ? false : true}
                        onClick={() => onClick(data.File)}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid container className="ticket-section-dialog-content mt-2">
                <TicketingMessage
                  side={"admin"}
                  data={data}
                  onClick={onClick}
                  noScroll={true}
                />
              </Grid>
              {
                !data.IsClosed &&
                <Grid container className="ticket-section-dialog-content mt-2">
                  <TicketTextBox
                    data={data}
                    text={newText}
                    changeText={handleNewMessage}
                    onChange={onChangeFile}
                    selectedFile={selectedFile}
                    onDeleteFile={onDeleteFile}
                    onDone={onSaveMessage}
                    side="admin"
                  />
                </Grid>
              }
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MuiButton
            onClick={() => setShowDialog(false)}
            title={<Translator id="enseraf" />}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}
